// import React from "react";
// import { TextFieldProps } from "@mui/material";
// import { CustomTextField } from "./CustomTextField";
// import { formatCurrencyInIndian } from "../../helpers/formatWithCommas";

// type CustomCurrencyFieldProps = TextFieldProps & {
//   value?: string | number;
// };

// export const CustomCurrencyField: React.FC<CustomCurrencyFieldProps> = ({
//   value,
//   onChange,
//   InputProps,
//   ...props
// }) => {
//   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     // Remove non-numeric characters to get raw value
//     const rawValue = e.target.value.replace(/[^0-9]/g, "");

//     // Mutate the event target value to return the raw number string
//     // This allows parent handlers to receive the clean numeric string/number
//     e.target.value = rawValue;

//     if (onChange) {
//       onChange(e);
//     }
//   };

//   return (
//     <CustomTextField
//       {...props}
//       value={value !== undefined && value !== null ? formatCurrencyInIndian(value) : ""}
//       onChange={handleChange}
//       InputProps={{
//         ...InputProps,
//         inputProps: {
//           ...InputProps?.inputProps,
//           style: {
//             textAlign: "right", // Align right as requested
//             ...InputProps?.inputProps?.style,
//           },
//         },
//       }}
//     />
//   );
// };

import React from "react";
import { TextFieldProps } from "@mui/material";
import { CustomTextField } from "./CustomTextField";

const stripFormatting = (v: string) => v.replace(/,/g, "");

const getCaretInfo = (value: string, caret: number) => {
  const left = value.slice(0, caret);
  const dotIndex = value.indexOf(".");

  // Decimal side
  if (dotIndex !== -1 && caret > dotIndex) {
    return {
      side: "dec" as const,
      offset:
        (left.match(/\d/g) || []).length -
        (value.slice(0, dotIndex).match(/\d/g) || []).length,
    };
  }

  // Integer side
  return {
    side: "int" as const,
    offset: (left.match(/\d/g) || []).length,
  };
};

const getCaretFromInfo = (
  formatted: string,
  info: { side: "int" | "dec"; offset: number },
) => {
  let digitCount = 0;
  let dotSeen = false;

  for (let i = 0; i < formatted.length; i++) {
    const ch = formatted[i];

    if (ch === ".") {
      dotSeen = true;
      if (info.side === "dec" && info.offset === 0) {
        return i + 1; // caret just after "."
      }
      continue;
    }

    if (/\d/.test(ch)) {
      if (
        (info.side === "int" && !dotSeen) ||
        (info.side === "dec" && dotSeen)
      ) {
        if (digitCount === info.offset) {
          return i;
        }
        digitCount++;
      }
    }
  }

  return formatted.length;
};

export const formatIndianNumber = (value: string) => {
  if (!value || value === "0") return "";

  const [int, dec] = value.toString().split(".");
  const lastThree = int.slice(-3);
  const other = int.slice(0, -3);

  const formattedInt = other
    ? other.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree
    : lastThree;

  return dec !== undefined ? `${formattedInt}.${dec}` : formattedInt;
};

type CustomCurrencyV5FieldProps = Omit<TextFieldProps, "value" | "onChange"> & {
  value?: string;
  onChange?: (value: string) => void;
  decimalScale?: number;
};

export const CustomCurrencyFieldV3: React.FC<CustomCurrencyV5FieldProps> = ({
  value = "",
  onChange,
  decimalScale = 2,
  InputProps,
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const caret = input.selectionStart ?? 0;

    // 1️⃣ Capture caret semantics
    const caretInfo = getCaretInfo(input.value, caret);

    // 2️⃣ Sanitize to RAW
    let raw = stripFormatting(input.value)
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");

    if (!raw.startsWith(".")) {
      raw = raw.replace(/^0+(?=\d)/, "");
    }

    // 3️⃣ Decimal scale control
    if (decimalScale !== undefined && raw.includes(".")) {
      const [i, d] = raw.split(".");
      raw = `${i}.${d.slice(0, decimalScale)}`;
    }

    const formatted = formatIndianNumber(raw);

    onChange?.(raw);

    // 4️⃣ Restore caret correctly (decimal-safe)
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const newCaret = getCaretFromInfo(formatted, caretInfo);
        inputRef.current.setSelectionRange(newCaret, newCaret);
      }
    });
  };

  const handleBlur = () => {
    if (!value) return;

    let raw = value;

    // Remove trailing decimal like "1."
    if (raw.endsWith(".")) {
      raw = raw.slice(0, -1);
    }

    onChange?.(raw);
  };

  return (
    <CustomTextField
      {...props}
      inputRef={inputRef}
      placeholder="0"
      value={formatIndianNumber(value)}
      onChange={handleChange}
      onBlur={handleBlur}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        ...InputProps,
        inputProps: {
          inputMode: "decimal",
          style: { textAlign: "right" },
        },
      }}
    />
  );
};
